import { Close, CloudUpload } from '@mui/icons-material';
import { Alert, Box, IconButton, Stack } from '@mui/material';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from '../../../hooks/useTranslation';
import type { AgentFormData } from '../../../interfaces/Agent';
import Button from '../../shared/Button/Button';
import EmployeeDirectoryToggle from './EmployeeDirectoryToggle';
import EmployeeDirectoryUploadModal from './EmployeeDirectoryUploadModal';

interface EmployeeDirectoryUploadProps {
  agentId: string;
  onRemove?: () => void;
  onToggleEmployeeDirectory?: () => void;
  disableEmployeeDirectoryToggle?: boolean;
  deferUpload?: boolean;
}

const EmployeeDirectoryUpload: React.FC<EmployeeDirectoryUploadProps> = ({
  agentId,
  onRemove,
  onToggleEmployeeDirectory,
  disableEmployeeDirectoryToggle = false,
  deferUpload = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();
  const { watch } = useFormContext<AgentFormData>();
  const stagedFile = deferUpload
    ? watch('employee_directory_staged_file')
    : null;

  return (
    <Box
      sx={{
        border: '1px solid lightgrey',
        padding: '1rem',
        borderRadius: '4px',
        backgroundColor: 'white',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: '1rem' }}
      >
        <Stack direction="row" alignItems="center" gap="1rem">
          <h3 style={{ margin: 0 }}>{t('Employee Directory')}</h3>
          {onToggleEmployeeDirectory && (
            <EmployeeDirectoryToggle
              checked={true}
              onChange={onToggleEmployeeDirectory}
              disabled={disableEmployeeDirectoryToggle}
            />
          )}
        </Stack>
        {onRemove && (
          <IconButton
            onClick={onRemove}
            sx={{
              height: '32px',
              width: '32px',
              color: 'error.main',
              border: '1px solid',
              borderColor: 'error.main',
              '&:hover': {
                backgroundColor: 'error.main',
                color: 'white',
              },
            }}
            title={t('Remove Employee Directory capability')}
          >
            <Close />
          </IconButton>
        )}
      </Stack>

      <Box sx={{ display: 'flex', justifyContent: 'center', my: '2rem' }}>
        <Button variant="outlined" onClick={() => setModalOpen(true)}>
          <Stack direction="row" alignItems="center" gap="0.5rem">
            <CloudUpload />
            {t('Upload .xlsx file')}
          </Stack>
        </Button>
      </Box>

      {stagedFile && (
        <Alert severity="info">
          <strong>{stagedFile.fileName}</strong>{' '}
          {t('File staged. It will be uploaded once your agent is created.')}
        </Alert>
      )}

      <EmployeeDirectoryUploadModal
        agentId={agentId}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        deferUpload={deferUpload}
      />
    </Box>
  );
};

export default EmployeeDirectoryUpload;
